import { useCallback } from 'react';
import type { UIMessage, UIMessagesMap } from '@viome/viome-base/src/usecase/SMP/SMP.model';

/**
 * Replaces [[key]] placeholders in a string with values from the params object.
 * Ported from shared-fe/src/utils/Localization.js
 */
export const replacePlaceholders = (
  str: string | undefined,
  params?: Record<string, string>,
): string => {
  if (!str || !params) return str ?? '';
  let result = str;
  for (const [key, value] of Object.entries(params)) {
    const escaped = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    result = result.replace(new RegExp(`\\[\\[${escaped}\\]\\]`, 'g'), value);
  }
  return result;
};

type MessageField = 'headerText' | 'headerHtml' | 'bodyText' | 'bodyHtml';

export const useUIMessages = (uiMessages: UIMessagesMap | undefined) => {
  const getMessage = useCallback(
    (internalName: string): UIMessage | undefined => {
      return uiMessages?.[internalName];
    },
    [uiMessages],
  );

  const getMessageText = useCallback(
    (
      internalName: string,
      field: MessageField = 'headerText',
      params?: Record<string, string>,
      fallback?: string,
    ): string => {
      const msg = uiMessages?.[internalName];
      const raw = msg?.[field];
      if (!raw) return fallback ?? '';
      return replacePlaceholders(raw, params);
    },
    [uiMessages],
  );

  return { getMessage, getMessageText };
};
